import { useState } from "react";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";

function Settings() {
  const demoMode = getIsDemoMode();
  const [notice, setNotice] = useState("");
  const [preferences, setPreferences] = useState({
    darkMode: true,
    notifications: true,
    aiInsights: true,
    budgetAlerts: true,
  });

  const user = {
    email: "owner@nexavo.in",
    name: "FinoraAI Owner",
    initials: "FO",
  };

  const togglePreference = (key) => {
    setPreferences((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const showPlaceholder = (message) => {
    setNotice(message);
  };

  const securityCards = [
    {
      icon: "PW",
      title: "Change Password",
      text: "Update credentials and keep your account protected.",
      enabled: false,
      comingSoon: true,
    },
    {
      icon: "SS",
      title: "Session Security",
      text: "Secure your account and monitor active sessions.",
      enabled: false,
      comingSoon: true,
    },
    {
      icon: "LA",
      title: "Login Activity",
      text: "Review recent sign-ins and access patterns.",
      enabled: false,
    },
    {
      icon: "DA",
      title: "Device Access",
      text: "Control trusted devices connected to this workspace.",
      enabled: false,
    },
  ];

  const aiFeatures = [
    { icon: "AI", label: "AI Insights Enabled" },
    { icon: "SC", label: "Smart Categorization" },
    { icon: "OCR", label: "OCR Receipt Scanner" },
    { icon: "PR", label: "Expense Prediction" },
    { icon: "RX", label: "AI Recommendations" },
  ];

  const dataActions = [
    { title: "Export Excel", text: "Use Reports export for live expense sheets.", disabled: false },
    { title: "Download Reports", text: "Generate monthly finance reports.", disabled: false },
    { title: "Backup Data", text: "Backup service placeholder.", disabled: true },
    { title: "Clear Demo Data", text: "Demo data is frontend-only and safe.", disabled: true },
  ];

  return (
    <div className="page settings-premium-page">
      <div className="settings-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">Workspace Preferences</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode Active</span>}
          </div>
          <h2>Settings</h2>
          <p>Manage your account, preferences, security, and app experience.</p>
        </div>
      </div>

      {demoMode && (
        <div className="demo-mode-notice">
          Demo settings actions are interactive and stay separate from personal data.
        </div>
      )}

      {notice && <div className="settings-notice">{notice}</div>}

      <div className="settings-layout-grid">
        <div className="settings-main-column">
          <section className="premium-panel settings-profile-card">
            <div className="settings-profile-avatar">{user.initials}</div>
            <div className="settings-profile-main">
              <span className="soft-badge">{demoMode ? "Demo Account" : "Owner Account"}</span>
              <h3>{user.name}</h3>
              <p>{user.email}</p>
              <span className="settings-status-badge secure">Secure Access Enabled</span>
            </div>
            <button
              type="button"
              className="glow-button"
              onClick={() => showPlaceholder("Edit Profile is frontend-only for now.")}
            >
              Edit Profile
            </button>
          </section>

          <section className="premium-panel settings-section-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Account & Security</span>
                <h3>Security Center</h3>
              </div>
            </div>

            <div className="settings-card-grid">
              {securityCards.map((item) => (
                <div key={item.title} className="settings-mini-card">
                  <span className="settings-icon-box">{item.icon}</span>
                  <div>
                    <h4 className="settings-card-heading">
                      <span>{item.title}</span>
                      {item.comingSoon && (
                        <span className="settings-coming-soon-badge">Coming Soon</span>
                      )}
                    </h4>
                    <p>{item.text}</p>
                  </div>
                  <label className="settings-switch is-disabled" aria-label={`${item.title} planned feature`}>
                    <input type="checkbox" checked={item.enabled} readOnly disabled />
                    <span></span>
                  </label>
                </div>
              ))}
            </div>
          </section>

          <section className="premium-panel settings-section-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">App Preferences</span>
                <h3>Experience Controls</h3>
              </div>
              <p>{demoMode ? "Demo Mode Active" : "Live Workspace"}</p>
            </div>

            <div className="settings-preference-list">
              <div className="settings-preference-row">
                <div>
                  <h4>Dark Mode</h4>
                  <p>Premium dark theme is active across the workspace.</p>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={preferences.darkMode}
                    onChange={() => togglePreference("darkMode")}
                  />
                  <span></span>
                </label>
              </div>

              <div className="settings-preference-row">
                <div>
                  <h4>Demo Mode Status</h4>
                  <p>{demoMode ? "Sample data is active." : "Real workspace data is active."}</p>
                </div>
                <span className={`settings-status-badge ${demoMode ? "demo" : "secure"}`}>
                  {demoMode ? "Demo Mode Active" : "Live Data"}
                </span>
              </div>

              <div className="settings-preference-row">
                <div>
                  <h4>Currency Display</h4>
                  <p>Indian Rupee formatting for reports and budgets.</p>
                </div>
                <span className="settings-currency-pill">INR</span>
              </div>

              <div className="settings-preference-row">
                <div>
                  <h4>Notification Preferences</h4>
                  <p>Receive finance updates and reminders.</p>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={preferences.notifications}
                    onChange={() => togglePreference("notifications")}
                  />
                  <span></span>
                </label>
              </div>

              <div className="settings-preference-row">
                <div>
                  <h4>AI Insights</h4>
                  <p>Enable assistant summaries and spending signals.</p>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={preferences.aiInsights}
                    onChange={() => togglePreference("aiInsights")}
                  />
                  <span></span>
                </label>
              </div>

              <div className="settings-preference-row">
                <div>
                  <h4>Monthly Budget Alerts</h4>
                  <p>Highlight categories nearing budget limits.</p>
                </div>
                <label className="settings-switch">
                  <input
                    type="checkbox"
                    checked={preferences.budgetAlerts}
                    onChange={() => togglePreference("budgetAlerts")}
                  />
                  <span></span>
                </label>
              </div>
            </div>
          </section>
        </div>

        <div className="settings-side-column">
          <section className="premium-panel settings-section-card settings-ai-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">AI Features Active</span>
                <h3>AI Assistant Preferences</h3>
              </div>
            </div>
            <p>Control how AI assists with expense tracking and analytics.</p>

            <div className="settings-ai-list">
              {aiFeatures.map((feature) => (
                <div key={feature.label} className="settings-ai-row">
                  <span className="settings-ai-dot"></span>
                  <span className="settings-ai-icon">{feature.icon}</span>
                  <strong>{feature.label}</strong>
                </div>
              ))}
            </div>
          </section>

          <section className="premium-panel settings-section-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Data & Export</span>
                <h3>Workspace Data</h3>
              </div>
            </div>

            <div className="settings-action-stack">
              {dataActions.map((action) => (
                <button
                  key={action.title}
                  type="button"
                  className="settings-action-button"
                  disabled={action.disabled}
                  onClick={() =>
                    showPlaceholder(
                      action.disabled
                        ? `${action.title} is not connected yet.`
                        : `${action.title} is available from Reports.`
                    )
                  }
                >
                  <span>{action.title}</span>
                  <small>{action.text}</small>
                </button>
              ))}
            </div>
          </section>

          <section className="premium-panel settings-about-card">
            <span className="soft-badge">AI Powered</span>
            <h3>Personal Expense Intelligence</h3>
            <div className="settings-about-list">
              <div>
                <span>App Name</span>
                <strong>FinoraAI</strong>
              </div>
              <div>
                <span>Version</span>
                <strong>1.0.0</strong>
              </div>
              <div>
                <span>Build Status</span>
                <strong className="settings-build-badge">Production Ready</strong>
              </div>
            </div>
            <p>AI Personal Finance Intelligence Platform</p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Settings;
